import { ArrowRightOnRectangleIcon, CheckIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useSignOut } from '../../hooks/useSignOut';
import { Dialog } from '../ui/Dialog';
import type { IconType } from '../ui/Chip';
import { ThemeSwitch } from '../ui/ThemeSwitch';
import { ContextSwitcher } from './ContextSwitcher';
import { NavItem } from './nav';

const ROW = 'flex min-h-14 w-full items-center gap-3 rounded-control px-3 text-left text-body text-strong hover:bg-hover';

export interface MoreSheetProps {
    open: boolean;
    onClose: () => void;
    /** Destinations that did not fit the bottom bar. */
    items: NavItem[];
}

/** Bottom sheet behind "More": remaining destinations, location, language, sign out. 56 px rows. */
export const MoreSheet = ({ open, onClose, items }: MoreSheetProps) => {
    const { t } = useTranslation();
    const { code, languages, change } = useLanguage();
    const signOut = useSignOut();
    const row = (to: string, Icon: IconType, label: string) => (
        <li key={to}>
            <Link to={to} onClick={onClose} className={ROW}>
                <Icon className="size-6" aria-hidden="true" />
                {label}
            </Link>
        </li>
    );
    return (
        <Dialog open={open} onClose={onClose} title={t('shell.more')}>
            <div className="mb-3">
                <ContextSwitcher stacked />
            </div>
            <ul>
                {items.map((i) => row(i.to, i.icon, t(i.labelKey)))}
                {row('/profile', UserCircleIcon, t('nav.profile'))}
            </ul>
            <div role="separator" className="my-2 h-px bg-line" />
            <p className="px-3 pb-1 text-caption text-muted">{t('shell.language')}</p>
            <div role="radiogroup" aria-label={t('shell.language')}>
                {languages.map((l) => (
                    <button key={l.code} type="button" role="radio" aria-checked={l.code === code} lang={l.code} onClick={() => change(l.code)} className={`${ROW} justify-between`}>
                        {l.label}
                        {l.code === code && <CheckIcon className="size-6" aria-hidden="true" />}
                    </button>
                ))}
            </div>
            <div role="separator" className="my-2 h-px bg-line" />
            <p className="px-3 pb-2 text-caption text-muted">{t('theme.label')}</p>
            <ThemeSwitch className="mx-3 mb-2" />
            <div role="separator" className="my-2 h-px bg-line" />
            <button
                type="button"
                onClick={() => {
                    onClose();
                    void signOut();
                }}
                className={ROW}
            >
                <ArrowRightOnRectangleIcon className="size-6" aria-hidden="true" />
                {t('shell.signOut')}
            </button>
        </Dialog>
    );
};
